<template>
  <!-- The same bar on every page (SPEC §4.3): signed in, the game list, a new game, the settings and the account;
       signed out, the logo only. The current page's item is marked. On the game page of a game in
       progress or just finished, the viewer's other games take the place of the new-game button
       (plan 2026-11-04-navigation-return.md §3.3, §3.4). -->
  <nav class="navbar" :class="{ mobile: $store.state.isMobile }">
    <router-link :to="user ? { name: 'list' } : { name: 'root' }" class="brand">
      <img :src="logo" :alt="t('app-name')">
    </router-link>
    <template v-if="user">
      <router-link :to="{ name: 'list' }" class="item" :class="{ current: current === 'list' }"
                   :aria-current="current === 'list' ? 'page' : null" @click.native.capture="onList">{{ t('nav-list') }}</router-link>
      <router-link v-if="!games" :to="{ name: 'create-game' }" class="new" :class="{ current: current === 'create-game' }"
                   :aria-current="current === 'create-game' ? 'page' : null">{{ t('nav-new') }}</router-link>
      <span v-else-if="chips.length" ref="chips" class="chips">
        <router-link v-for="c in shownChips" :key="c.g.id" ref="chip" :to="{ name: 'game', params: { id: c.g.id } }"
                     class="chip" :class="c.kind" :title="c.status">
          <span v-if="c.word" class="word">{{ c.word }}</span><span class="stone" :class="c.colour"></span><span class="chip-name">{{ c.name }}</span>
        </router-link>
        <button v-if="hidden.length" ref="more" type="button" class="chip more" :aria-label="t('nav-more-games-label', { n: hidden.length })"
                :title="t('nav-more-games-label', { n: hidden.length })" :aria-expanded="moreOpen ? 'true' : 'false'"
                @click="moreOpen = !moreOpen">{{ t('nav-more-games', { n: hidden.length }) }}</button>
      </span>
      <span class="space"></span>
      <router-link :to="{ name: 'settings' }" class="item" :class="{ current: current === 'settings' }"
                   :aria-current="current === 'settings' ? 'page' : null">{{ t('nav-settings') }}</router-link>
      <router-link :to="{ name: 'account' }" class="item name" :class="{ current: current === 'account' }"
                   :aria-current="current === 'account' ? 'page' : null">{{ user.displayName }}</router-link>
    </template>
    <!-- The games that didn't fit (+n): one row each, with what it waits for or how it ended. -->
    <template v-if="moreOpen && hidden.length">
      <div class="more-shade" @click="moreOpen = false"></div>
      <div class="more-list" role="menu" :style="moreStyle">
        <router-link v-for="c in hidden" :key="c.g.id" :to="{ name: 'game', params: { id: c.g.id } }" class="more-row" role="menuitem">
          <span class="more-name"><span class="stone" :class="c.colour"></span><span class="chip-name">{{ c.name }}</span></span>
          <span class="more-status" :class="c.kind">{{ c.status }}</span>
        </router-link>
      </div>
    </template>
  </nav>
</template>

<script>
import imgLogo from '@/assets/slowgo-sm.png';
import api from '@/api/api';
import text from '@/text';
import { t } from '@/texts';
import { isMyMove, opponent, lastTime, finishTime, byTime, resultFor } from '@/mygames';

// A finished game counts as just finished for this long (plan 2026-11-04-navigation-return.md §3.4).
const JUST_FINISHED = 3 * 24 * 3600 * 1000;

export default {
  name: 'NavBar',

  props: {
    // On the game page: { current: <game id>, pending: true while it isn't known yet whether the
    // bar shows chips }. Absent elsewhere, and on a game page whose game isn't in progress or just
    // finished: the bar is then as on any page, with the new-game button.
    games: { type: Object, default: null },
  },

  data() {
    return { logo: imgLogo, chips: [], fit: Infinity, moreOpen: false, moreStyle: {} };
  },

  computed: {
    user() {
      return this.$store.state.user;
    },

    // The bar item of the page shown, if any.
    current() {
      const name = this.$route.name;
      return ['list', 'create-game', 'settings', 'account'].includes(name) ? name : null;
    },

    shownChips() {
      return this.chips.slice(0, this.fit);
    },

    hidden() {
      return this.chips.slice(this.fit);
    },

    wanted() {
      return !!(this.user && this.games && !this.games.pending);
    },
  },

  watch: {
    wanted: { immediate: true, handler(now) { if (now) this.load(); } },
    moreOpen(open) {
      if (open) this.$nextTick(this.placeMore);
    },
  },

  mounted() {
    window.addEventListener('resize', this.remeasure);
    document.addEventListener('visibilitychange', this.onVisible);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.remeasure);
    document.removeEventListener('visibilitychange', this.onVisible);
  },

  methods: {
    t,

    // Tapping 棋局 on the list itself goes back to its top (plan §3.2); a capture listener, so that it
    // runs before the link navigates and the page shown is still the one tapped on.
    onList() {
      if (this.current === 'list') window.scrollTo(0, 0);
    },

    // The viewer's other games in progress, and those that just finished and they haven't seen
    // (plan §6.3); nothing on failure.
    async load() {
      const me = this.user;
      const since = new Date(Date.now() - JUST_FINISHED).toISOString().replace(/\.\d+Z$/, 'Z');
      const get = params => api.getList(api.GAMES, { player: me.id, ...params }).catch(() => []);
      const [playing, ended] = await Promise.all([
        get({ status: 'in-progress' }),
        get({ status: 'completed', completedSince: since, resultUnseenBy: me.id }),
      ]);
      if (!this.wanted) return;
      const others = l => (l || []).filter(g => g.id !== this.games.current);
      const mine = others(playing).filter(g => isMyMove(g, me));
      const waiting = others(playing).filter(g => !isMyMove(g, me));
      const finished = others(ended).sort((a, b) => finishTime(b) - finishTime(a));
      this.chips = [
        ...finished.map(g => this.chip(g, resultFor(g, me) || 'done')),
        ...byTime(mine, 1).map(g => this.chip(g, 'mine')),
        ...byTime(waiting, -1).map(g => this.chip(g, 'wait')),
      ];
      this.remeasure();
    },

    // One chip: the opponent's stone and name as the list's card shows them, and what the game
    // waits for or how it ended (SPEC §4.3).
    chip(g, kind) {
      const o = opponent(g, this.user);
      const name = o.you ? t('seat-you') : o.player ? o.player.displayName : t('seat-empty');
      const word = kind === 'won' ? t('nav-chip-won') : kind === 'lost' ? t('nav-chip-lost') : '';
      let status;
      if (kind === 'mine') status = g.seal ? t(g.seal.sealedBy === this.user.id ? 'list-sealed-own' : 'list-sealed-other') : t('list-your-move');
      else if (kind === 'wait') status = g.seal ? t(g.seal.sealedBy === this.user.id ? 'list-sealed-own' : 'list-sealed-other') : t('list-section-waiting');
      else status = text.status(g);
      return { g, kind, colour: o.colour, name, word, status, time: lastTime(g) };
    },

    // Shows as many chips as fit between 棋局 and 设置, the rest behind +n.
    remeasure() {
      this.fit = Infinity;
      this.$nextTick(this.measure);
    },

    measure() {
      const box = this.$refs.chips;
      const els = [].concat(this.$refs.chip || []).map(c => c.$el || c);
      if (!box || !els.length) return;
      const width = box.clientWidth;
      const left = box.getBoundingClientRect().left;
      const right = el => el.getBoundingClientRect().right - left;
      if (right(els[els.length - 1]) <= width + 0.5) return;
      const reserve = this.$store.state.isMobile ? 40 : 46;
      let n = 0;
      while (n < els.length && right(els[n]) <= width - reserve) n++;
      this.fit = n;
    },

    placeMore() {
      const more = this.$refs.more;
      if (!more) return;
      const r = more.getBoundingClientRect();
      const width = Math.min(260, window.innerWidth - 16);
      this.moreStyle = { left: `${Math.max(8, Math.min(r.left - 60, window.innerWidth - width - 8))}px`, top: `${r.bottom + 6}px`, width: `${width}px` };
    },

    onVisible() {
      if (document.visibilityState === 'visible' && this.wanted) this.load();
    },
  },
}
</script>

<style scoped>
.navbar {
  display: flex;
  align-items: center;
  background: #faedcd;
  padding: 5px 16px 5px 10px;
  font-size: 16px;
}

/* On a phone the bar is 44 px high, to leave the board room. */
.navbar.mobile {
  padding: 6px 10px 6px 8px;
}

.brand {
  display: flex;
  margin-right: 16px;
}

.brand img {
  height: 50px;
}

.mobile .brand {
  margin-right: 12px;
}

.mobile .brand img {
  height: 32px;
}

/* Starting a game is drawn as a small outlined button, so it doesn't get lost among the links. */
.new {
  margin-left: 14px;
  padding: 3px 8px;
  border: 1px solid #c9a96a;
  border-radius: 6px;
  background: #fff8e6;
  color: #2c3e50;
  text-decoration: none;
  white-space: nowrap;
}

.new.current {
  font-weight: bold;
}

.space {
  flex: 1;
}

.item {
  color: #595959;
  text-decoration: none;
  white-space: nowrap;
  padding: 6px 0;
  margin-left: 18px;
}

.item:first-of-type {
  margin-left: 0;
}

.item:hover {
  color: #0175ff;
}

.item.current {
  color: #2c3e50;
  font-weight: bold;
  text-decoration: underline;
  text-underline-offset: 4px;
}

/* A long display name is cut short; everything else keeps its place. */
.item.name {
  max-width: 8em;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: none;
}

.mobile .item.name {
  max-width: 6em;
}

/* The viewer's other games (SPEC §4.3): they give way first when the bar is short. */
.chips {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 16px;
  min-width: 0;
  flex: 0 1 auto;
  overflow: hidden;
}

.mobile .chips {
  gap: 5px;
  margin-left: 10px;
}

.chip {
  display: inline-flex;
  align-items: center;
  flex: none;
  box-sizing: border-box;
  height: 30px;
  padding: 0 9px;
  border: 1px solid #ccc;
  border-radius: 15px;
  background: #fff;
  color: #595959;
  font-size: 15px;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
}

.mobile .chip {
  height: 26px;
  padding: 0 6px;
  border-radius: 13px;
  font-size: 13px;
}

/* Blue only for "your move"; green and red only for results (2026-10-21 review). */
.chip.mine {
  border-color: #0175ff;
  background: #eef5ff;
  color: #0175ff;
  font-weight: bold;
}

.chip.won {
  border-color: #0a0;
  background: #e3f5e3;
  color: #0a0;
  font-weight: bold;
}

.chip.lost {
  border-color: #d00;
  background: #fde8e8;
  color: #d00;
  font-weight: bold;
}

.chip.more {
  padding: 0 7px;
  font: inherit;
  font-size: 15px;
}

.mobile .chip.more {
  font-size: 13px;
}

.chip .word {
  margin-right: 3px;
}

.chip-name {
  max-width: 6em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mobile .chip-name {
  max-width: 3.2em;
}

.stone {
  display: inline-block;
  flex: none;
  box-sizing: border-box;
  width: .8em;
  height: .8em;
  margin-right: 2px;
  border-radius: 50%;
}

.stone.black {
  background: #111;
}

.stone.white {
  background: #fff;
  border: 1px solid #333;
}

.more-shade {
  position: fixed;
  inset: 0;
  z-index: 19;
}

.more-list {
  position: fixed;
  z-index: 20;
  box-sizing: border-box;
  padding: 4px 0;
  border: 1px solid #c8b28a;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 14px rgba(0, 0, 0, .18);
  text-align: left;
}

.more-row {
  display: flex;
  flex-direction: column;
  padding: 8px 12px;
  border-bottom: 1px solid #f0e6d6;
  color: #2c3e50;
  font-size: 15px;
  text-decoration: none;
}

.more-row:last-child {
  border-bottom: none;
}

.more-name {
  display: flex;
  align-items: center;
  min-width: 0;
}

.more-name .chip-name {
  max-width: none;
}

.more-status {
  font-size: 13px;
  color: #888;
}

.more-status.mine {
  color: #0175ff;
  font-weight: bold;
}

.more-status.won {
  color: #0a0;
}

.more-status.lost {
  color: #d00;
}
</style>
